/**
 * Withdrawal form — load the order's products.
 *
 * Sends the current order number (and email for guests) to the custom
 * `withdrawal_populate` route, then swaps the whole form with the re-rendered
 * markup returned by the server. The re-rendered form turns the products field
 * into a group of checkboxes (one per product of the order) or, on failure,
 * carries the relevant field error.
 *
 * Two triggers:
 * - logged-in users pick an order in the <select> → auto populate on change
 *   (the select lives in a [data-withdrawal-populate-on-change] wrapper),
 * - guests type an order number and click the [data-withdrawal-populate] button.
 *
 * Each loaded product also has a return-quantity input next to its checkbox; the
 * quantity is only enabled while the product is checked (the server renders the
 * initial disabled state, this keeps it in sync on toggle).
 *
 * Config is provided by PHP through `window.gt_configuration.gilbertWithdrawal`:
 *   { populateUrl: string }
 *
 * CSRF is covered by the form's own nonce, which is part of the posted FormData.
 */
export default function Withdrawal($) {
  const WRAPPER_SELECTOR = '[data-withdrawal-form]';
  const BUTTON_SELECTOR = '[data-withdrawal-populate]';
  // The logged-in order picker lives in a [data-withdrawal-populate-on-change]
  // wrapper; match its <select> directly so it works regardless of nesting.
  const ON_CHANGE_SELECTOR = '[data-withdrawal-populate-on-change] select, select[name="order_number"]';
  const PRODUCT_CHECKBOX_SELECTOR = '[data-withdrawal-product-checkbox]';
  const PRODUCT_ROW_SELECTOR = '[data-withdrawal-product-row]';
  const PRODUCT_QTY_SELECTOR = '[data-withdrawal-product-qty]';

  function config() {
    return window.gt_configuration?.gilbertWithdrawal ?? null;
  }

  /**
   * @param {HTMLElement} triggerEl Any element inside the form wrapper that kicks
   *                                off the populate (the button or the select).
   */
  function populate(triggerEl) {
    const cfg = config();
    if (!cfg || !cfg.populateUrl) {
      console.error('[Withdrawal] missing gt_configuration.gilbertWithdrawal.populateUrl');
      return;
    }

    const wrapper = triggerEl.closest(WRAPPER_SELECTOR);
    const form = wrapper?.querySelector('form');
    if (!form) {
      console.error('[Withdrawal] could not find the form wrapper / form element');
      return;
    }

    // Send the current form values (incl. the form's own CSRF nonce) so the
    // re-rendered form keeps what the user already filled in.
    const data = new FormData(form);

    const params = new URLSearchParams();
    for (const [key, value] of data.entries()) {
      params.append(key, value);
    }

    const $trigger = $(triggerEl);
    $trigger.prop('disabled', true);

    $.post(cfg.populateUrl, params.toString())
      .done((response) => {
        if (!response || typeof response.html !== 'string') return;
        $(wrapper).replaceWith(response.html);
      })
      .fail((xhr) => {
        // A 403/4xx still carries a re-rendered form with the error attached.
        const html = xhr?.responseJSON?.html;
        if (typeof html === 'string') {
          $(wrapper).replaceWith(html);
        } else {
          console.error('[Withdrawal] populate error:', xhr.statusText);
        }
      })
      .always(() => {
        $trigger.prop('disabled', false);
      });
  }

  // Event delegation so the handlers survive the form being swapped in place.

  // Guest: explicit button.
  $(document).on('click', BUTTON_SELECTOR, function (event) {
    event.preventDefault();
    populate(this);
  });

  // Logged-in: auto populate as soon as an order is selected.
  $(document).on('change', ON_CHANGE_SELECTOR, function () {
    if (!this.value) return; // ignore the empty "Select an order" placeholder
    populate(this);
  });

  // Enable/disable a product's quantity input as its checkbox is toggled.
  $(document).on('change', PRODUCT_CHECKBOX_SELECTOR, function () {
    const row = this.closest(PRODUCT_ROW_SELECTOR);
    const qty = row?.querySelector(PRODUCT_QTY_SELECTOR);
    if (!qty) return;

    qty.disabled = !this.checked;
    if (this.checked && (!qty.value || Number(qty.value) < 1)) {
      qty.value = 1; // sensible default when first selecting the product
    }
  });
}
